import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from '../utils/api';
import { FRAMEWORK_TRACKS, PRODUCTION_TRACK } from '../config/tracksCatalog';
import TrackCard, { TrackStatus } from './TrackCard';
import LucideIcon from './LucideIcon';

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

interface TopicProgressEntry {
  completed: number;
  completedChallenges: string[];
}

/**
 * Production & Observability catalog. Gated on having started at least one
 * framework track -- tracing/evaluation/guardrails only make sense once
 * there's an agent to instrument, matching the "Unlocks after [a framework]"
 * gate in docs/build-plan/03-core-learning-app.md's Frameworks Catalog
 * section (progression gating, not a paid tier -- no billing exists here).
 */
const ProductionCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [topicProgress, setTopicProgress] = useState<Record<string, TopicProgressEntry>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    API.get('/progress')
      .then((response: any) => {
        if (cancelled) return;
        setTopicProgress(response?.[0]?.topicProgress || {});
      })
      .catch(() => { /* fail open to empty progress, not a broken page */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const unlocked = FRAMEWORK_TRACKS.some((t) => (topicProgress[t.slug]?.completed || 0) > 0);
  const completed = topicProgress[PRODUCTION_TRACK.slug]?.completed || 0;
  const total = PRODUCTION_TRACK.lessons.length;
  const status: TrackStatus = !unlocked ? 'locked' : completed >= total ? 'complete' : completed > 0 ? 'in-progress' : 'not-started';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div className="ws-fade-up" style={panel}>
        <p className="ws-mono" style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
          Missions / Production &amp; Observability
        </p>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Production &amp; Observability</h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>
          Building an agent is one skill. Shipping one you can trust is another.
        </p>
        {loading && <p className="ws-mono" style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>Loading progress&hellip;</p>}
      </div>

      {!loading && !unlocked && (
        <div className="ws-fade-up" style={{ ...panel, background: 'var(--surface-sunken)', borderStyle: 'dashed', display: 'flex', alignItems: 'center', gap: 14 }}>
          <LucideIcon name="status-locked" size={22} />
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Start a framework track first</p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              Tracing, evaluation, and guardrails all assume you already have an agent to instrument.{' '}
              <Link to="/frameworks" style={{ color: 'var(--ember-strong)', fontWeight: 700 }}>Pick a framework</Link> and complete
              at least one lesson to unlock this track.
            </p>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 420 }}>
        <TrackCard
          track={PRODUCTION_TRACK}
          status={status}
          completed={completed}
          total={total}
          lockedReason={!unlocked ? 'Unlocks after your first lesson in any framework track.' : undefined}
          onClick={unlocked ? () => navigate(`/tracks/${PRODUCTION_TRACK.slug}`) : undefined}
        />
      </div>
    </div>
  );
};

export default ProductionCatalog;
